import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { safeNum, scrollTriggerRange } from './dot-cms-aniamtion.helper';
import { AnimationType, AnimationDynamicSettings } from './animation/animations.model';

gsap.registerPlugin(ScrollTrigger);

export const scrollAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  'scroll-spin': (s, el, opts) => {
    const spinCount = safeNum(s.spin_count, 1);
    const scaleVal = safeNum(s.scale, 1);
    const isCCW = /counter|anti|ccw/i.test(String(s.direction || ''));
    const rotDir = isCCW ? -360 : 360;

    return gsap.to(el, {
      rotation: rotDir * spinCount,
      scale: scaleVal,
      ...opts, // includes safe duration, delay, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-spin'].start, // CMS or fallback
        end: s.end ?? scrollTriggerRange['scroll-spin'].end,
        scrub: s.speed ?? true, // CMS or default true
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false, // markers only if enabled
      },
    });
  },

  'scroll-move': (s, el, opts) => {
    // Read DotCMS values with fallbacks
    const angle = safeNum(s.angle, 0); // degrees
    const distance = safeNum(s.distance, 100); // px

    // Convert angle to radians
    const rad = (angle * Math.PI) / 180;

    // Compute displacement
    const x = Math.cos(rad) * distance;
    const y = Math.sin(rad) * distance;

    return gsap.from(el, {
      x,
      y,
      ...opts, // duration, delay, ease, repeat, yoyo etc.

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-move'].start,
        end: s.end ?? scrollTriggerRange['scroll-move'].end,
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-expand': (s, el, opts) => {
    const speed = safeNum(s.speed, 1); // controls scrub speed

    // Optional: use direction to expand more strongly along x/y
    let transform;
    if (s.direction) {
      const dir = String(s.direction).toLowerCase();
      if (dir === 'up') transform = { y: 60 };
      else if (dir === 'down') transform = { y: -60 };
      else if (dir === 'left') transform = { x: 60 };
      else if (dir === 'right') transform = { x: -60 };
    }

    return gsap.to(el, {
      scale: s.scale,
      ...transform,
      ...opts, // includes duration, delay, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-expand'].start,
        end: s.end ?? scrollTriggerRange['scroll-expand'].end,
        scrub: s.scrub ?? speed, // scrub speed from CMS
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-shrink': (s, el, opts) => {
    const speed = safeNum(s.speed, 1); // scrub speed

    let transform;
    if (s.direction) {
      const dir = String(s.direction).toLowerCase();
      if (dir === 'up') transform = { y: 60 };
      else if (dir === 'down') transform = { y: -60 };
      else if (dir === 'left') transform = { x: 60 };
      else if (dir === 'right') transform = { x: -60 };
    }

    return gsap.to(el, {
      scale: s.scale,
      ...transform,
      ...opts, // includes delay, duration, ease, repeat, yoyo

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-shrink'].start,
        scrub: s.scrub ?? speed,
        end: s.end ?? scrollTriggerRange['scroll-shrink'].end,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-fade': (s, el, opts) => {
    const startOpacity = safeNum(s.startOpacity, 0);

    return gsap.fromTo(
      el,
      { opacity: startOpacity },
      {
        opacity: 1,
        y: 0,
        stagger: 0.06,
        ...opts, // includes delay, duration, ease, repeat, yoyo
        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-fade'].start,
          end : s.end ?? scrollTriggerRange['scroll-fade'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none none',
        },
      }
    );
  },

  'scroll-slide': (s, el, opts) => {
    let dx = 0,
      dy = 0;
    if (s.direction === 'left') {
      dx = 100;
    } else if (s.direction === 'right') {
      dx = -100;
    } else if (s.direction === 'up') {
      dy = 100;
    } else if (s.direction === 'down') {
      dy = -100;
    }

    return gsap.fromTo(
      el,
      { x: dx, y: dy, opacity: s.startOpacity ?? 0 },
      {
        x: 0,
        y: 0,
        opacity: 1,
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-slide'].start,
          end: s.end ?? scrollTriggerRange['scroll-slide'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-blur': (s, el, opts) => {
    const blur = safeNum(s.blur_intensity, 10);

    return gsap.fromTo(
      el,
      { filter: `blur(${blur}px)` },
      {
        filter: 'blur(0px)',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-blur'].start,
          end: s.end ?? scrollTriggerRange['scroll-blur'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-reveal': (s, el, _opts) => {
    let clipFrom;
    if (s.direction === 'left') {
      clipFrom = 'inset(0 100% 0 0)';
    } else if (s.direction === 'right') {
      clipFrom = 'inset(0 0 0 100%)';
    } else if (s.direction === 'up') {
      clipFrom = 'inset(100% 0 0 0)';
    } else if (s.direction === 'down') {
      clipFrom = 'inset(0 0 100% 0)';
    }

    return gsap.from(el, {
      clipPath: clipFrom,
      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-reveal'].start,
        end: s.end ?? scrollTriggerRange['scroll-reveal'].end,
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-shape': (s, el, opts) => {
    const shape = String(s.shape ?? 'square').toLowerCase();

    // Map DotCMS shapes → clip-path definitions
    const shapeMap: Record<string, string> = {
      square: 'inset(0% 0% 0% 0%)',
      circle: 'circle(0% at 50% 50%)',
      oval: 'ellipse(0% 0% at 50% 50%)',
      diamond: `polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)`,
    };

    const startShape = shapeMap[shape] ?? shapeMap['square'];
    let endShape: string;

    if (shape === 'circle') {
      endShape = 'circle(75% at 50% 50%)';
    } else if (shape === 'oval') {
      endShape = 'ellipse(75% 75% at 50% 50%)';
    } else if (shape === 'diamond') {
      // diamond just scales from tiny → full
      endShape = `polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)`;
    } else {
      endShape = 'inset(0% 0% 0% 0%)';
    }

    // Reset before animating
    gsap.set(el, {
      clipPath: startShape,
      webkitClipPath: startShape,
      opacity: s.startOpacity ?? 0,
    });

    return gsap.to(el, {
      clipPath: endShape,
      webkitClipPath: endShape,
      opacity: 1,
      ...opts,

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-shape'].start,
        end: s.end ?? scrollTriggerRange['scroll-shape'].end ,
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-arc': (s, el, opts) => {
    let x = 0,
      y = 0,
      rotation = 0;

    switch (s.direction) {
      case 'left':
        x = -90;
        y = -90 * 0.5;
        rotation = -45;
        break;
      case 'right':
        x = 90;
        y = -90 * 0.5;
        rotation = 45;
        break;
      case 'up':
        y = -90;
        x = 90 * 0.5;
        rotation = 45;
        break;
      case 'down':
        y = 90;
        x = 90 * 0.5;
        rotation = -45;
        break;
    }

    // Use a motionPath-like bezier for arc effect
    return gsap.fromTo(
      el,
      { x, y, rotation, opacity: 0 },
      {
        x: 0,
        y: 0,
        rotation: 0,
        opacity: 1,
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-arc'].start,
          end: s.end ?? scrollTriggerRange['scroll-arc'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-3dspin': (s, el, opts) => {
    const rotation = safeNum(s.rotation, 360);
    return gsap.from(el, {
      rotationY: rotation,
      opacity: 1,
      ...opts,

      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-3dspin'].start,
        end: s.end ?? scrollTriggerRange['scroll-3dspin'].end,
        scrub: s.scrub ?? true,
        toggleActions: s.toggleActions ?? 'play none none reverse',
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-fly': (s, el, opts) => {
    const fromValues: Record<string, { x: number; y: number }> = {
      up: { y: 100, x: 25 },
      down: { y: -100, x: 25 },
      right: { x: -100, y: 25 },
      left: { x: 100, y: 25 },
    };

    const flyFrom = fromValues[s.direction] || { x: 100, y: -100 };

    return gsap.from(
      el,

      {
        ...flyFrom,
        opacity: 1,
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-fly'].start,
          end: s.end ?? scrollTriggerRange['scroll-fly'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-turn': (s, el, opts) => {
    let rotationProp, startAngle;
    switch (s.direction) {
      case 'left':
        {
          rotationProp = 'rotationY';
          startAngle = -180;
        }
        break;
      case 'right':
        {
          rotationProp = 'rotationY';
          startAngle = 180;
        }
        break;
      case 'up':
        {
          rotationProp = 'rotationX';
          startAngle = 180;
        }
        break;
      case 'down':
        {
          rotationProp = 'rotationX';
          startAngle = -180;
        }
        break;
    }

    return gsap.fromTo(
      el,
      {
        [rotationProp]: startAngle,
        transformOrigin: '50% 50%',
      },
      {
        [rotationProp]: 0,
        scale: s.scale,
        ease: s.easing ?? 'power2.out',
        ...opts,

        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-turn'].start,
          end: s.end ?? scrollTriggerRange['scroll-turn'].end ,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-tilt': (s, el, opts) => {
    const dir = String(s.direction || 'up').toLowerCase();
    let rotateProp: 'rotateX' | 'rotateY';
    let tiltStart: number;
    let dx, dy;

    if (dir === 'up' || dir === 'down') {
      rotateProp = 'rotateX';
      tiltStart = dir === 'up' ? 45 : -45;
      dy = dir === 'up' ? 100 : -100;

    } else {
      rotateProp = 'rotateY';
      tiltStart = dir === 'left' ? -45 : 45;
      dx = dir === 'left' ? -100 : 100;
    }

    return gsap.fromTo(
      el,
      {
        x: dx,
        y: dy,
        opacity: 0,
        [rotateProp]: tiltStart,
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        [rotateProp]: 0,
        ease: s.easing ?? 'power2.out',
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-tilt'].start,
          end: s.end ?? scrollTriggerRange['scroll-tilt'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-flip': (s, el, opts) => {
    const rotation = safeNum(s.rotation, 180); // default flip angle
    const orientation = String(s.orientation || 'horizontal').toLowerCase();

    // Axis selection
    const axis = orientation === 'vertical' ? 'rotationX' : 'rotationY';

    return gsap.to(
      el,
      {
        [axis]: rotation,
        opacity: 1,
        ease: s.easing ?? 'power2.out',
        duration: s.speed ?? 1.2,
        transformOrigin:"50% 50%",
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-flip'].start,
          end: s.end ?? scrollTriggerRange['scroll-flip'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-parallax': (s, el, opts) => {
    return gsap.to(el, {
      y: s.speed * 50,
      ease: 'none',
      ...opts,
      scrollTrigger: {
        trigger: el,
        start: s.start ?? scrollTriggerRange['scroll-parallax'].start,
        end: s.end ?? scrollTriggerRange['scroll-parallax'].end,
        scrub: s.speed,
        markers: s.markers ?? false,
      },
    });
  },

  'scroll-shutters': (s, el, opts) => {
    // const parts = safeNum(s.number_of_parts, 4);
    const stagger = safeNum(s.stagger, 0.1);

    return gsap.fromTo(
      el,
      {
        opacity: 0,
        clipPath: 'inset(0 100% 0 0)',
      },
      {
        opacity: 1,
        clipPath: 'inset(0 0% 0 0)',
        stagger: stagger,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? 'top 80%',
          end: s.end ?? 'bottom 0%',
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },

  'scroll-stretch': (s, el, opts) => {
    const percentage = safeNum(s.stretch_percentage, 150);
    const scaleValue = percentage / 100;

    return gsap.from(
      el,
      {
        scale:scaleValue,
        ...opts,
        scrollTrigger: {
          trigger: el,
          start: s.start ?? scrollTriggerRange['scroll-stretch'].start,
          end: s.end ?? scrollTriggerRange['scroll-stretch'].end,
          scrub: s.scrub ?? true,
          toggleActions: s.toggleActions ?? 'play none none reverse',
          markers: s.markers ?? false,
        },
      }
    );
  },
};
